import type { SnackbarOrigin } from '@material-ui/core'
import { append, reject, takeLast, whereEq } from 'ramda'
import type React from 'react'
import { createContext, FC, type Reducer, useCallback, useContext, useReducer } from 'react'
import ReactDOM from 'react-dom'
import { v4 as uuid } from 'uuid'
import { MessageTypes } from 'core/components/notifications/model'
import ToastContainer, { type ToastOptions } from 'core/components/notifications/ToastContainer'
import { except, pipe } from 'utils/fp'

const toastsTimeout = 10000
const concurrentToasts = 5

interface ToastReducerAction {
  type: 'add' | 'remove'
  payload: ToastOptions
}

export const toastReducer: Reducer<ToastOptions[], ToastReducerAction> = (
  state,
  { type, payload },
) => {
  switch (type) {
    case 'add':
      return pipe(
        takeLast(concurrentToasts - 1),
        // Remove previous duplicated messages to prevent flooding the screen
        reject(
          whereEq({
            text: payload.text,
            variant: payload.variant,
          }),
        ),
        append(payload),
      )(state)
    case 'remove':
      return except(payload, state)
    default:
      return state
  }
}

type ShowToastFn = (
  text: string,
  type?: MessageTypes,
  timeout?: number,
  align?: SnackbarOrigin,
) => void

const ToastContext = createContext<ShowToastFn>(null)

const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [toasts, dispatch] = useReducer(toastReducer, [])
  const showToast: ShowToastFn = useCallback(
    (text, variant = MessageTypes.info, timeout = toastsTimeout, align) => {
      const payload: ToastOptions = {
        id: uuid(),
        text,
        variant,
        isOpen: true,
        onClose: () => dispatch({ type: 'remove', payload }),
        timeout,
        align,
      }
      dispatch({ type: 'add', payload })
    },
    [],
  )

  return (
    <ToastContext.Provider value={showToast}>
      {ReactDOM.createPortal(
        <ToastContainer toasts={toasts} toastsTimeout={toastsTimeout} />,
        document.body,
      )}
      {children}
    </ToastContext.Provider>
  )
}

export default ToastProvider

export const withToast = (Component) => (props) => (
  <ToastContext.Consumer>
    {(showToast) => <Component {...props} showToast={showToast} />}
  </ToastContext.Consumer>
)

export const useToast: () => ShowToastFn = () => {
  return useContext(ToastContext)
}
